import { useQuery, useMutation, QueryClient } from '@tanstack/react-query';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { ResourceBudgetProposalAssociationT } from 'routes/BudgetModule/config/types';
import { EnrichedOperationT, OperationBalanceT } from './types';
import { BudgetBys } from 'business/constants';
import { snakify } from 'helpers/syntaxHelper';

type OperationInfo = {
    name: string;
    softwareId: string;
    isDraft: boolean;
};

export const useGetOperationInfo = (operationSoftwareId: string) =>
    useQuery<OperationInfo, HttpError>({
        queryKey: ['operation', operationSoftwareId],

        queryFn: async () => {
            const response = await RQAPICallWithData<OperationInfo>({
                endpoint: `budget/operation/${operationSoftwareId}`,
                method: 'GET',
            });
            return response.content.data;
        },
    });

export const useGetOperationAssociations = (
    operationSoftwareId: string,
    budgetBy: BudgetBys | null,
) =>
    useQuery({
        queryKey: ['operationAssociations', operationSoftwareId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                ResourceBudgetProposalAssociationT[]
            >({
                endpoint: `budget/operation/${operationSoftwareId}/associations?budget_by=${budgetBy}`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled: budgetBy != null,
    });

/**
 * Hook to fetch an enriched operation
 * or create it if it does not exist.
 */
export const useGetEnrichedOperation = (
    operationSoftwareId: string,
    budgetBy: BudgetBys | null,
) => {
    const endpoint = `budget/enriched_operation/${operationSoftwareId}?budget_by=${budgetBy}`;

    return useQuery<EnrichedOperationT, HttpError>({
        queryKey: ['enriched_operation', operationSoftwareId],

        queryFn: async () => {
            const getResponse = await RQAPICallWithData<EnrichedOperationT>({
                endpoint,
                method: 'GET',
            });
            if (
                getResponse.content.message === 'success.no_enriched_operation'
            ) {
                // operation exists but the enriched operation does not.
                const postResponse =
                    await RQAPICallWithData<EnrichedOperationT>({
                        endpoint,
                        method: 'POST',
                    });
                return postResponse.content.data;
            }
            return getResponse.content.data;
        },

        enabled: budgetBy != null,
    });
};

/**
 * Hook to update the description of an enriched operation.
 */
export const useEnrichedOperationMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async ({
            operationSoftwareId,
            description,
        }: {
            operationSoftwareId: string;
            description: string;
        }) => {
            const response = await RQAPICallWithData<EnrichedOperationT>({
                endpoint: `budget/enriched_operation/${operationSoftwareId}`,
                method: 'PATCH',
                body: { description },
            });
            return response.content.data;
        },

        onSuccess: (enrichedOperation: EnrichedOperationT): void => {
            queryClient.setQueryData(
                ['enriched_operation', enrichedOperation.operationSoftwareId],
                (oldEnrichedOperation: EnrichedOperationT) => ({
                    ...oldEnrichedOperation,
                    description: enrichedOperation.description,
                }),
            );
            trackEventBudget(
                TRACKABLE_EVENTS.budget.general.operation360.editDescription,
            );
        },
    });

/**
 * Hook to fetch an enriched operation
 * or create it if it does not exist.
 */
export const useGetOperationBalance = (
    operationSoftwareId: string,
    budgetBy: BudgetBys,
    exercice: number,
) => {
    const endpoint = `budget/operation/balance/${operationSoftwareId}?budget_by=${snakify(budgetBy)}&exercice=${exercice}`;

    return useQuery<OperationBalanceT[], HttpError>({
        queryKey: [
            'operation_balance',
            operationSoftwareId,
            budgetBy,
            exercice,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<OperationBalanceT[]>({
                endpoint,
                method: 'GET',
            });
            return response.content.data;
        },
    });
};
